import classNames from "classnames";
import { memo, useCallback, useState } from "react";
import { Menu } from "@design-system/Menu/Menu.lib";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { Button } from "@design-system/Button/Button";
import { PlainModal } from "@design-system/Modal/Modal";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { CellContextWithDragHandle } from "@design-system/ViewTable/ViewTable";
import { useDeleteClassMutation, useClasses } from "@api/class.api";
import { getDescendantIds } from "@helpers/class";
import { pluralize } from "@common/helpers/string";
import { MAX_CLASS_DEPTH } from "@common/constants/class";
import { PlusIcon, DragHandle6DotIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { RenameClassModal } from "../children/RenameClassModal";
import { CreateClassModal } from "../children/CreateClassModal";
import { ClassRow } from "../viewUtils";
import styles from "../ClassesSettings.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

type ActionsCellContext = CellContextWithDragHandle<ClassRow, unknown>;

export const ActionsCell = memo(function ActionsCell({
    row,
    rowDragHandle,
    table,
}: ActionsCellContext) {
    const item = row.original;
    const classInstance = item.classInstance;
    const isSubclass = classInstance.parentClassId !== null;
    const deleteClass = useDeleteClassMutation();
    const { classesMap } = useClasses();

    const meta = table.options.meta as
        | { onSubclassAdded?: (parentId: string) => void }
        | undefined;

    // A node can receive children only while its depth is below the deepest
    // allowed level (depth MAX_CLASS_DEPTH - 1 is the last addable level).
    const canHaveChildren = item.depth < MAX_CLASS_DEPTH - 1;
    const isAtMaxDepth = item.depth === MAX_CLASS_DEPTH - 1;
    const showAddButton = item.depth < MAX_CLASS_DEPTH;

    const addButtonTooltip = !canHaveChildren
        ? "Maximum nesting depth reached"
        : "Add subclass";

    const [showAddClassModal, setShowAddClassModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const descendantCount = showDeleteModal
        ? getDescendantIds({ classesMap, id: classInstance.id }).size
        : 0;

    const toggleAddModal = useCallback(() => {
        setShowAddClassModal((prev) => !prev);
    }, []);

    return (
        <ViewTableCell captureClicks>
            <HStack align="center" justify="end" gap={12}>
                <span
                    className={classNames(
                        styles.actionDragHandle,
                        viewTableStyles.showOnHover,
                        {
                            [styles.dragHandleDragging]:
                                rowDragHandle?.isDragging,
                        },
                    )}
                    {...(rowDragHandle?.attributes ?? {})}
                    {...(rowDragHandle?.listeners ?? {})}
                    onClick={(event) => event.stopPropagation()}
                >
                    <DragHandle6DotIcon />
                </span>
                <CaptureClicks>
                    <Menu.More
                        className={classNames(
                            viewTableStyles.showOnHover,
                            styles.actionIconContainer,
                            styles.dropdownButton,
                        )}
                    >
                        <Menu.Item onClick={() => setShowRenameModal(true)}>
                            Rename
                        </Menu.Item>
                        <Menu.Item
                            onClick={() => setShowDeleteModal(true)}
                            destructive
                        >
                            Delete
                        </Menu.Item>
                    </Menu.More>
                </CaptureClicks>

                {showAddButton && (
                    <CaptureClicks>
                        <Tooltip content={addButtonTooltip}>
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                className={viewTableStyles.showOnHover}
                                onClick={
                                    canHaveChildren ? toggleAddModal : undefined
                                }
                                disabled={isAtMaxDepth}
                                aria-label={addButtonTooltip}
                            >
                                <PlusIcon />
                            </Button>
                        </Tooltip>
                    </CaptureClicks>
                )}
            </HStack>

            {showRenameModal && (
                <PlainModal
                    show={showRenameModal}
                    onHide={() => setShowRenameModal(false)}
                >
                    <RenameClassModal
                        classInstance={classInstance}
                        onHide={() => setShowRenameModal(false)}
                    />
                </PlainModal>
            )}

            {showDeleteModal && (
                <ConfirmationModal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    title={`Are you sure that you want to delete ${isSubclass ? "subclass" : "class"} ${classInstance.label}?`}
                    no="Cancel"
                    question={
                        descendantCount > 0
                            ? `This will also delete ${descendantCount} ${pluralize("subclass", descendantCount)} and unassign their transactions.`
                            : ""
                    }
                    onReject={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(false);
                    }}
                    onConfirm={() =>
                        deleteClass.mutateAsync(classInstance.id).then(() => {
                            setShowDeleteModal(false);
                        })
                    }
                    yes="Delete"
                />
            )}

            {showAddClassModal && (
                <PlainModal show={showAddClassModal} onHide={toggleAddModal}>
                    <CreateClassModal
                        parentClassId={classInstance.id}
                        onHide={toggleAddModal}
                        onCreated={() =>
                            meta?.onSubclassAdded?.(classInstance.id)
                        }
                    />
                </PlainModal>
            )}
        </ViewTableCell>
    );
});
